import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import {
  LayoutDashboard, ArrowLeftRight, ClipboardList, Users as UsersIcon,
  DollarSign, UserCog, FileBarChart2, LogOut, Globe, Menu, X
} from "lucide-react";

export default function AppLayout() {
  const { user, logout, t, lang, changeLang } = useApp();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const links = [
    { to: "/", label: t("dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/new", label: t("new_transaction"), icon: ArrowLeftRight, testid: "nav-new-tx" },
    { to: "/transactions", label: t("transactions"), icon: ClipboardList, testid: "nav-transactions" },
    { to: "/customers", label: t("customers"), icon: UsersIcon, testid: "nav-customers" },
    { to: "/rates", label: t("rates"), icon: DollarSign, testid: "nav-rates" },
    { to: "/reports", label: t("reports"), icon: FileBarChart2, testid: "nav-reports" },
  ];
  if (user?.role === "admin") {
    links.push({ to: "/users", label: t("users"), icon: UserCog, testid: "nav-users" });
  }

  const roleBadge = {
    admin: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    teller: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    cashier: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  }[user?.role] || "bg-slate-500/15 text-slate-400";

  const renderLinks = (onClick) =>
    links.map((l) => (
      <NavLink
        key={l.to}
        to={l.to}
        data-testid={l.testid}
        end={l.to === "/"}
        onClick={onClick}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
            isActive
              ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
              : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"
          }`
        }
      >
        <l.icon className="w-4 h-4" />
        {l.label}
      </NavLink>
    ));

  return (
    <div className="min-h-screen flex">
      {/* Mobile drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 glass border-r border-slate-800 flex flex-col">
            <div className="px-5 py-5 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center text-slate-900 font-bold">R</div>
                <div>
                  <div className="text-sm font-semibold tracking-tight">Riyadh-Nusantara</div>
                  <div className="text-[10px] text-slate-500 tracking-wider uppercase">FX Desk</div>
                </div>
              </div>
              <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} className="p-2 text-slate-400 hover:text-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">{renderLinks(() => setMenuOpen(false))}</nav>
            <div className="p-3 border-t border-slate-800">
              <div className="flex items-center gap-2 px-3 py-2">
                <div className="w-8 h-8 rounded-full bg-slate-800 grid place-items-center text-xs font-semibold">
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{user?.name}</div>
                  <div className={`pill border ${roleBadge}`}>{t(user?.role)}</div>
                </div>
              </div>
              <button
                data-testid="mobile-logout-btn"
                onClick={async () => { await logout(); nav("/login"); }}
                className="w-full mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-red-400 hover:bg-red-500/5 transition"
              >
                <LogOut className="w-4 h-4" /> {t("logout")}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Sidebar */}
      <aside className="w-64 hidden md:flex flex-col border-r border-slate-800 glass sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center text-slate-900 font-bold">R</div>
            <div>
              <div className="text-sm font-semibold tracking-tight">Riyadh-Nusantara</div>
              <div className="text-[10px] text-slate-500 tracking-wider uppercase">FX Desk</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {renderLinks()}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-slate-800 grid place-items-center text-xs font-semibold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{user?.name}</div>
              <div className={`pill border ${roleBadge}`}>{t(user?.role)}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="w-full mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-red-400 hover:bg-red-500/5 transition"
          >
            <LogOut className="w-4 h-4" /> {t("logout")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 glass border-b border-slate-800 px-4 md:px-6 h-14 flex items-center gap-4">
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMenuOpen(true)}
            className="md:hidden p-2 -ml-1 text-slate-300 hover:text-amber-400 transition"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="text-xs text-slate-500 tracking-wider uppercase hidden md:block">{t("branch")}</div>
          <div className="hidden md:block h-4 w-px bg-slate-700" />
          <div className="flex-1" />
          <div className="flex items-center gap-1 border border-slate-800 rounded-full p-1">
            <Globe className="w-3.5 h-3.5 text-slate-500 ml-1.5" />
            <button
              data-testid="lang-en"
              onClick={() => changeLang("en")}
              className={`text-xs px-2 py-0.5 rounded-full ${lang === "en" ? "bg-amber-500 text-slate-900 font-semibold" : "text-slate-400"}`}
            >EN</button>
            <button
              data-testid="lang-id"
              onClick={() => changeLang("id")}
              className={`text-xs px-2 py-0.5 rounded-full ${lang === "id" ? "bg-amber-500 text-slate-900 font-semibold" : "text-slate-400"}`}
            >ID</button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 lg:p-8 fade-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
